import { useEffect, useState } from 'react';

import { siteConfig } from '@/lib/i18n/en';
import { FormValidator, useForm, UseFormActions } from '@/hooks/useForm';

import Button from '@/decodo/components/atoms/Button';
import { Checkbox } from '@/decodo/components/atoms/Checkbox';
import { Input } from '@/decodo/components/atoms/Input';
import LinkButton from '@/decodo/components/atoms/LinkButton';
import { Text } from '@/decodo/components/atoms/Text';

import { FileUploadField } from './FileUploadField';
import { countries } from '../constants/countries';
import {
  EMAIL_REGEX,
  INFO_MAX_LENGTH,
  MAX_RESUME_FILE_SIZE,
  MB,
  SUPPORTED_FILE_TYPES,
} from '../constants/form';
import { formatList } from '../helpers';

import IconCheck from '~/icons/check.svg';
import IconSpinner from '~/svg/IconSpinner.svg';
import { TelephoneInput } from '@/decodo/components/molecules/TelephoneInput';

export const DATA_PROTECTION_URL = '/legal/data-protection';

export interface ApplicationFormSchema {
  fullName: string;
  email: string;
  countryCode: string;
  phoneNumber: string;
  linkedInProfile?: string;
  resume?: File;
  consent: boolean;
  information?: string;
}

const validator: FormValidator<ApplicationFormSchema> = {
  fullName: (value) => {
    if (typeof value !== 'string') return;
    if (!value) return 'Full name is required';
    if (value.length > 50) return 'Max of 50 characters allowed';
  },
  email: (value) => {
    if (typeof value !== 'string') return;
    if (!value) return 'Email is required';
    if (!EMAIL_REGEX.test(value)) return 'Invalid email';
  },
  resume: (value) => {
    if (!value) return 'Resume is required';
    if (!(value instanceof File)) return 'Resume must be a file';
    if (
      !SUPPORTED_FILE_TYPES.some((type) =>
        value.name.toLowerCase().endsWith(type)
      )
    )
      return `Choose a ${formatList(SUPPORTED_FILE_TYPES, 'or')} file`;
    if (value.size > MAX_RESUME_FILE_SIZE)
      return `Choose a file lesser than ${MAX_RESUME_FILE_SIZE / MB}MB`;
  },
  countryCode: (value) => {
    if (typeof value !== 'string') return;
    if (!value) return 'Country Code is required';
    if (
      value &&
      !/^\+\d{1,3}$/.test(value) &&
      countries.find((country) => country.code === parseInt(value)) ===
        undefined
    )
      return 'Invalid country code';
  },
  phoneNumber: (value) => {
    if (typeof value !== 'string') return;
    if (!value) return 'Phone number is required';
    if (value && !/^\d{6,}$/.test(value)) return 'Invalid phone number';
  },
  consent: (value) => {
    if (typeof value !== 'boolean') return;
    if (!value) return 'You have to agree with Privacy Notice';
  },
};

interface ApplicationFormProps {
  onSubmit: (
    formState: ApplicationFormSchema,
    formActions: UseFormActions
  ) => void;
  loading?: boolean;
  error?: Error;
  data?: unknown;
}

export function ApplicationForm({
  loading,
  onSubmit,
  error,
  data,
}: ApplicationFormProps) {
  const {
    handleSubmit,
    getFieldProps,
    isValid: isFormValid,
    setFieldValue,
    values,
  } = useForm<ApplicationFormSchema>({
    initialValues: {
      fullName: '',
      email: '',
      countryCode: '+1',
      phoneNumber: '',
      linkedInProfile: '',
      consent: false,
      information: '',
      resume: undefined,
    },
    validator,
    validateOnBlur: true,
    validateOnChange: true,
  });

  const [isShowingStatus, setIsShowingStatus] = useState(false);

  useEffect(() => {
    if (error) {
      setIsShowingStatus(true);
      setTimeout(() => {
        setIsShowingStatus(false);
      }, 5000);
    }
  }, [error]);
  useEffect(() => {
    if (data) {
      setIsShowingStatus(true);
      setTimeout(() => {
        setIsShowingStatus(false);
      }, 5000);
    }
  }, [data]);

  const renderStatus = () => {
    if (loading) return <IconSpinner className='h-6 w-6' />;
    if (!isShowingStatus) return 'Submit';
    if (error) return 'Failed to submit';
    return 'Application submitted successfully';
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className='flex flex-col gap-1 md:gap-5'
      data-tracker-component='form'
    >
      <Text variant='h3' classNames={{ wrapper: 'pb-8 font-serif' }}>
        Personal details
      </Text>
      <Input
        label='Full Name'
        isRequired
        {...getFieldProps('fullName')}
        classNames={{ root: 'text-gray-500' }}
      />
      <Input
        label='Email'
        isRequired
        {...getFieldProps('email')}
        classNames={{ root: 'text-gray-500' }}
      />

      <TelephoneInput
        countryCodeProps={{
          ...getFieldProps('countryCode'),
          onOptionChange: (value) => setFieldValue('countryCode', value),
          label: 'Phone Number',
          isRequired: true,
          popoverProps: {
            triggerProps: {
              'data-tracker-context': 'country_code',
            },
          },
        }}
        phoneNumberProps={{
          ...getFieldProps('phoneNumber'),
          isRequired: true,
        }}
      />
      <Text
        variant='subtitle-l'
        classNames={{ wrapper: 'pt-4 pb-8 md:pt-3 md:pb-7 font-serif' }}
      >
        Links
      </Text>
      <FileUploadField
        label='Resume/CV (You can also attach Linkedin CV)*'
        inputLabel='ATTACH (.PDF/.DOC/.PNG)'
        {...getFieldProps('resume')}
        value={values.resume ? [values.resume] : []}
        acceptedFileTypes={SUPPORTED_FILE_TYPES}
        onChange={(files) => setFieldValue('resume', files[0])}
        onFileRemove={() => setFieldValue('resume', undefined)}
      />
      <Input
        label='Link to your profile (Linkedin/Github/Portfolio etc.) URL'
        {...getFieldProps('linkedInProfile')}
        classNames={{ label: { root: 'text-gray-500' } }}
      />
      <div className='pb-5 md:pb-8'>
        <Text
          variant='subtitle-l'
          classNames={{ wrapper: 'pb-8 md:pb-12 pt-4 md:pt-3 font-serif' }}
        >
          Additional Information
        </Text>
        <Text variant='body-m' classNames={{ wrapper: 'text-gray-500 pb-2' }}>
          Add a cover letter or anything else you want to share
        </Text>

        <Input
          as='textarea'
          {...getFieldProps('information')}
          maxLength={INFO_MAX_LENGTH}
        />
      </div>
      <div className='pb-5 md:pb-7'>
        <Checkbox
          {...getFieldProps('consent')}
          value='consent'
          checked={values.consent}
          classNames={{
            label: 'font-mono',
          }}
          label={
            <span>
              I have read and understood the
              <LinkButton
                variant='text'
                color='tertiary'
                href={DATA_PROTECTION_URL}
                openInNewTab
                slim
                classNames={{ root: 'text-body-l pl-1 underline' }}
              >
                Privacy Notice
              </LinkButton>
            </span>
          }
        />
      </div>
      <Text variant='body-m' classNames={{ wrapper: 'text-gray-500' }}>
        {siteConfig.title} is committed to building an inclusive workplace with
        a diversity of backgrounds. We welcome applications from people of all
        ages, gender identities, sexual orientations, racial identities,
        ethnicities, religious beliefs, and disability statuses. To reduce the
        chance of unconscious bias, you may submit your CV without a photograph
        or other redundant information like age or marital status.
      </Text>
      <Button
        type='submit'
        fullwidth
        color={isShowingStatus && !!error ? 'destructive' : undefined}
        classNames={{
          root: 'mt-8 md:mt-1',
          inner: loading ? 'animate-[spin_2s_linear_infinite]' : '',
        }}
        leftSection={isShowingStatus && !error && <IconCheck />}
        disabled={loading || !isFormValid || isShowingStatus}
      >
        {renderStatus()}
      </Button>
    </form>
  );
}
